import styled from "@emotion/styled";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";
import React, { useEffect, useState } from "react";
import SelectBox from "../../components/selectBox";
import theme from "../../theme";
import 'react-quill/dist/quill.snow.css';
import TextEditor from "./textEditor";
import AlertModal from "../../components/alertModal";
import {
  ClipboardBulletIcon,
  WarningIcon,
} from "../../assets/icons/icons";
import usePostNotice from "../../hooks/usePostNotice";
import { useLocation, useNavigate } from "react-router-dom";
import { NoticeDetail } from "../../types/types";
import usePutNotice from "../../hooks/usePutNotice";
import userStorage from "../../utils/userStorage";
import { Button } from "../../components/common/Button";
import { deleteNoticeImage } from "../../api/notice";
import { PageLayout } from "../../components/PageLayout";
import { useWindowSize } from "@reactuses/core";
import { SrLable } from "../../components/common/SrLabel";
import { useI18n } from "../../locales";

const { md } = theme.screen;

interface LocationParam {
  state: {
    data: NoticeDetail;
    id: number;
  };
}

export interface UploadNoticeImages {
  index: number; // 에디터 내에서의 위치
  file: File; // 저장된 파일
  url: string;
}

// 라우터 : notice-admin
const WriteNotice: React.FC<{}> = () => {
  const { t } = useI18n();
  const { width } = useWindowSize();
  const { state } = useLocation() as LocationParam;

  const navigate = useNavigate();
  
  const menuItems = [
    { value: 1, title: `WEB ${t.notice.title}` },
    { value: 2, title: `APP ${t.notice.title}` },
  ];
  const [part, setPart] = useState(state?.data?.part || 1);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(state?.data?.title || "");
  const [contents, setContents] = useState(state?.data?.contents || "");
  const [beforeHtml, setBeforeHtml] = useState("");
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [counter, setCounter] = useState(0);
  const [imgFiles, setImgFiles] = useState<UploadNoticeImages[]>([]); // 새로 업로드된 이미지
  const [initialImgFiles, setInitialImgFiles] = useState<
    UploadNoticeImages[]
  >([]); // 기존 이미지 (수정)
  const [deleteImgFile, setDeleteImgFile] = useState<UploadNoticeImages[]>(
    []
  );
  const [initialContents, setInitialContents] = useState(""); // 수정하기 모드에서 초기 데이터

  const user = userStorage.get();
  const { mutate: postNotice } = usePostNotice({
    title,
    contents,
    part,
    writer_idx: user?.idx as number,
    area: user?.area as string,
  });

  const { mutate: patchNotice, isPending } = usePutNotice({
    title,
    contents,
    part,
    idx: state?.data?.idx.toString(),
  });

  const onPressCancel = () => {
    setOpenCancelModal(true);
  };

  const onPressNo = (
    selected: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    selected(false);
  };

  const onPressComplete = () => {
    setOpenConfirmModal(true);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event: SelectChangeEvent) => {
    setPart(Number(event.target.value));
  };

  const onChangeTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.currentTarget.value);
  };

  const goBack = () => {
    // 수정하기인 경우
    if (state?.data) {
      // 지우려고 저장했던 이미지들 중 초기 컨텐츠에 없는 애들
      let deleteImage = deleteImgFile.filter(
        (el) => !initialContents.includes(el.url)
      );

      if (deleteImage.length > 0) {
        deleteImage.map((el) => {
          // console.log(deleteImage);
          deleteNoticeImageFn(el.url.substring(9));
        });
      }
      navigate(`/notice/${state.data.idx}`, {
        replace: true,
        state: { from: "home" },
      });
    } else {
      // 신규 작성인 경우 - 전체 지움
      imgFiles.map((el) => {
        deleteNoticeImageFn(el.url.substring(9));
      });
      deleteImgFile.map((el) => {
        deleteNoticeImageFn(el.url.substring(9));
      });
      navigate(-1);
    }
    setDeleteImgFile([]);
  };

  const handleSubmit = () => {
    // state?.data ? patchNotice : postNotice
    if (state?.data) {
      patchNotice();
    } else {
      postNotice();
    }

    deleteImgFile.map((el) => {
      deleteNoticeImageFn(el.url.substring(9));
    });
    setDeleteImgFile([]);
  };

  const deleteNoticeImageFn = async (imageName: string) => {
    const res = await deleteNoticeImage({ fileName: imageName });
    if (res.httpStatus === "OK") {
      // console.log("이미지 삭제 성공");
      setImgFiles([]);
    } else {
      // console.log("삭제 오류", res);
    }
  };

  useEffect(() => {
    if (state?.data) {
      setInitialContents(state.data.contents);
    }
  }, [state]);

  // console.log("부모컴포넌트 상태", imgFiles);
  // console.log("contents", contents);
  // console.log(state.data);
  // console.log("initialContents", initialContents);

  return (
    <PageLayout
      bodyHeight="calc(100vh - 106px)"
      bodyMinHeight="1105px"
      withBreadcrumb
    >
      <Title>{state?.data ? t.notice.editTitle : t.notice.write}</Title>
      <WriteHeader>
        <SelectBox
          open={open}
          handleOpen={handleOpen}
          handleClose={handleClose}
          handleChange={handleChange}
          menuItems={menuItems}
          title=""
          filter={part}
          width={width <= md ? 120 : 201}
          fontSize={width <= md ? `12px` : undefined}
          isWhite={true}
          mr="0px"
          srLabel={t.notice.category}
        />
        <SrLable labelText={t.notice.noticeTitle} id="noticeTitle" />
        <CustomInput
          id="noticeTitle"
          placeholder={t.notice.titlePlaceholder}
          onChange={onChangeTitle}
          value={title}
          maxLength={100}
        />
      </WriteHeader>
      <TextEditor
        counter={counter}
        setCounter={setCounter}
        beforeHtml={beforeHtml}
        setBeforeHtml={setBeforeHtml}
        value={contents}
        onChangeValue={setContents}
        imgFiles={imgFiles}
        setImgFiles={setImgFiles}
        initialImgFiles={initialImgFiles}
        setInitialImgFiles={setInitialImgFiles}
        deleteImgFile={deleteImgFile}
        setDeleteImgFile={setDeleteImgFile}
        isNewWrite={state?.data ? false : true}
      />
      <BtnWrapper>
        <Button
          onClick={onPressCancel}
          bgColor={theme.statusColor.CLOSE}
          hoverColor={theme.statusColor.CLOSE_HOVER}
        >
          {t.notice.cancel}
        </Button>
        <Button
          onClick={onPressComplete}
          bgColor={theme.statusColor.MAIN}
          hoverColor={theme.statusColor.MAIN_HOVER}
          isDisabled={
            beforeHtml.length > 1999 ||
            counter == 0 ||
            counter == 1 ||
            !title
          }
          isLoading={isPending}
        >
          {width > md && (state?.data ? t.notice.edit + " " + t.notice.complete : t.notice.write + " " + t.notice.complete)}
          {width <= md && t.notice.complete}
        </Button>
      </BtnWrapper>

      <AlertModal
        open={openConfirmModal}
        onPressNo={() => onPressNo(setOpenConfirmModal)}
        title={t.notice.registerConfirm}
        content={t.notice.registerContent}
        confirmText={t.notice.confirm}
        icon={<ClipboardBulletIcon />}
        onPressYes={handleSubmit}
      />
      <AlertModal
        open={openCancelModal}
        onPressNo={() => onPressNo(setOpenCancelModal)}
        title={t.notice.cancelConfirm}
        content={t.notice.cancelContent}
        confirmText={t.notice.confirm}
        icon={<WarningIcon />}
        onPressYes={goBack}
      />
    </PageLayout>
  );
};

const Title = styled.h1`
  font-weight: 700;
  font-size: 20px;
  line-height: 18px;
  color: ${theme.color.neutral_600};
  padding-bottom: 15px;
  border-bottom: ${`3px solid ${theme.color.neutral_200}`};
  margin-bottom: 20px;
  padding-left: 10px;

  @media (max-width: ${md}px) {
    font-size: 18px;
  }
`;

const WriteHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 15px;
  @media (max-width: ${md}px) {
    gap: 10px;
    margin-bottom: 10px;
    justify-content: center;
  }
`;

const CustomInput = styled.input`
  position: relative;
  top: 2px;
  border: none;
  flex: 1;
  height: 40px;
  border-radius: 10px;
  padding-left: 37px;
  padding-right: 15px;
  @media (max-width: ${md}px) {
    padding-left: 20px;
    font-size: 12px;
    height: 24px;
    top: 0px;
    border-radius: 5px;
  }
`;

const BtnWrapper = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;

  button {
    width: 137px;
    height: 40px;
  }

  @media (max-width: ${md}px) {
    margin-top: 20px;
    button {
      width: 70px;
      font-size: 12px;
    }
  }
`;

export default WriteNotice;
